import { MqttStorage } from '@weavix/mqtt/src/persistence/mqtt-storage';
import { LazyStorage } from './lazy-storage';

/** A stored message and the user whose client stored it; older builds stored the bare message. */
type OwnedMessage = { owner?: string, packet: string };

/** Why stored messages were dropped for a user: another user stored them, or no user was on record. */
export type MqttOutboxClaim = { reason: 'user-changed' | 'no-owner', dropped: number };

export class WebMqttStorage implements MqttStorage {
    /** One map per type, shared by every view so they never overwrite each other's writes. */
    private static readonly maps = new Map<string, LazyStorage<OwnedMessage | string>>();
    private readonly messages: LazyStorage<OwnedMessage | string>;

    /**
     * A view of the stored messages of `type` that holds only `owner`'s: what it stores is tagged with `owner`, and it
     * reads back nothing stored by anyone else, including other tabs and older builds. */
    constructor(type: 'incoming' | 'outgoing', public readonly owner?: string) {
        const key = `mqtt-${type}`;
        if (!WebMqttStorage.maps.has(key)) WebMqttStorage.maps.set(key, new LazyStorage(key));
        this.messages = WebMqttStorage.maps.get(key);
    }

    /** Drops every stored message that is not this view's owner's. Returns why and how many, or undefined when none were. */
    async dropOthers(): Promise<MqttOutboxClaim | undefined> {
        const others = Object.entries(await this.messages.map()).filter(([, message]) => !this.owns(message));
        if (!others.length) return undefined;
        await Promise.all(others.map(([key]) => this.messages.remove(key)));
        const reason = others.some(([, message]) => typeof message !== 'string' && message.owner) ? 'user-changed' : 'no-owner';
        return { reason, dropped: others.length };
    }

    /** Clears every stored message, whoever stored it, and returns how many there were. */
    async drop(): Promise<number> {
        const dropped = Object.keys(await this.messages.map()).length;
        this.clear();
        return dropped;
    }

    clear() {
        this.messages.clear();
    }

    async get(key: string) {
        const message = await this.messages.get(this.keyFor(key));
        return message && this.owns(message) ? (message as OwnedMessage).packet : undefined;
    }

    set(key: string, packet: string) {
        return this.messages.set(this.keyFor(key), { owner: this.owner, packet });
    }

    async getAll() {
        return Object.values(await this.messages.map()).filter(message => this.owns(message)).map(message => (message as OwnedMessage).packet);
    }

    remove(key: string) {
        return this.messages.remove(this.keyFor(key));
    }

    /** The stored key, scoped to the owner so two users' clients reusing a message id never collide. */
    private keyFor(key: string) {
        return `${this.owner ?? ''}/${key}`;
    }

    private owns(message: OwnedMessage | string) {
        return typeof message !== 'string' && message.owner === this.owner;
    }
}
